import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "@tanstack/react-router";
import { PageShell } from "@project/user/components/page-shell";
import {
  ImageReveal,
  Reveal,
  Stagger,
  StaggerItem,
} from "@project/user/components/motion";
import { Badge } from "@project/components/ui/badge";
import { Button } from "@project/components/ui/button";
import {
  ArrowLeft,
  Clock,
  CalendarX,
  Stethoscope,
  HeartPulse,
  Award,
  CheckCircle2,
  Heart,
  Building2,
} from "lucide-react";
import Marquee from "react-fast-marquee";
import { HealthcareDonationModal } from "@project/user/components/healthcare-donation-modal";
import { useI18n } from "@project/lib/use-i18n";

import healthcare1 from "@project/assets/Helthcare-1.webp";
import healthcare2 from "@project/assets/Healthcare-2.jpg";
import healthcare3 from "@project/assets/Healthcare-3.jpg";

import {
  hospitalProfileService,
  medicalStaffService,
} from "@project/services/admin";
import type {
  HospitalProfile,
  NibhavYojanaItem,
} from "@project/types/hospital-profile";
import type { MedicalStaff } from "@project/types/medical-staff";

export default function HealthcareDetailPage() {
  const { t, lang } = useI18n();
  const { id } = useParams({ from: "/_user/_care/healthcare/$id" as any });
  const [profiles, setProfiles] = useState<HospitalProfile[]>([]);
  const [staffList, setStaffList] = useState<MedicalStaff[]>([]);
  const [loading, setLoading] = useState(true);
  const [donationModalOpen, setDonationModalOpen] = useState(false);
  const [selectedScheme, setSelectedScheme] = useState<NibhavYojanaItem | null>(
    null
  );

  const bgImages = useMemo(() => [healthcare1, healthcare2, healthcare3], []);
  const [bgIndex, setBgIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setBgIndex(prev => (prev + 1) % bgImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [bgImages.length]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [profilesRes, staffRes] = await Promise.all([
          hospitalProfileService.listPage({ page: 1, page_size: 100 }),
          medicalStaffService.listPage({ page: 1, page_size: 100 }),
        ]);
        setProfiles(
          (profilesRes.data || [])
            .filter(p => p.is_active)
            .sort((a, b) => (a.order_by ?? 0) - (b.order_by ?? 0))
        );
        setStaffList(staffRes.data || []);
      } catch (err) {
        console.error("Failed to load healthcare detail page data", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const activeProfile = useMemo(() => {
    return profiles.find(p => p.id === Number(id)) || null;
  }, [profiles, id]);

  const isPhysioCenter = useMemo(() => {
    if (!activeProfile) return false;
    const name = (activeProfile.hospital_name || "").toLowerCase();
    // Strictly matches only Bhagwan Mahavir Physiotherapy / Rehab center
    return (
      name.includes("physio") ||
      name.includes("rehab") ||
      name.includes("ફિઝિયો") ||
      name.includes("પક્ષઘાત")
    );
  }, [activeProfile]);

  const isMedicalOpdCenter = useMemo(() => {
    if (!activeProfile) return false;
    const name = (activeProfile.hospital_name || "").toLowerCase();
    return (
      (name.includes("medical") ||
        name.includes("મેડિકલ") ||
        name.includes("opd") ||
        name.includes("ઓપીડી") ||
        name.includes("centre") ||
        name.includes("center")) &&
      !name.includes("physio") &&
      !name.includes("dental") &&
      !name.includes("eye") &&
      !name.includes("દાંત") &&
      !name.includes("આંખ") &&
      !name.includes("ફિઝિયો") &&
      !name.includes("પક્ષઘાત")
    );
  }, [activeProfile]);

  const linkedDoctors = useMemo(() => {
    if (!activeProfile) {
      return [];
    }
    return staffList.filter(s => (activeProfile.doctors || []).includes(s.id));
  }, [activeProfile, staffList]);

  const nibhavYojanaList = useMemo<NibhavYojanaItem[]>(() => {
    if (!activeProfile) {
      return [];
    }
    const raw =
      (activeProfile as any).nibhav_yojana ||
      (activeProfile as any).maintenance_schemes;
    if (Array.isArray(raw)) {
      return raw;
    }
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch {
        // Ignore invalid JSON format
      }
    }
    return [];
  }, [activeProfile]);

  const handleDonateScheme = (scheme: NibhavYojanaItem) => {
    setSelectedScheme(scheme);
    setDonationModalOpen(true);
  };

  // Check if the right side has any content sections (facilities, equipment, doctors, nurses, emergency contact)
  const hasRightSideContent = useMemo(() => {
    if (!activeProfile) return false;
    const hasFacilities = Boolean(
      activeProfile.facilities &&
      activeProfile.facilities.some(f => typeof f === "string" && f.trim().length > 0)
    );
    const hasEquipments = Boolean(
      activeProfile.equipments &&
      activeProfile.equipments.some(e => typeof e === "string" && e.trim().length > 0)
    );
    const hasDoctors = linkedDoctors.length > 0;
    const hasNurses = Boolean(
      activeProfile.nurses &&
      activeProfile.nurses.some(
        n => (n.name && n.name.trim().length > 0) || (n.role && n.role.trim().length > 0)
      )
    );
    const hasEmergency = Boolean(
      (activeProfile.emergency_contact_name && activeProfile.emergency_contact_name.trim().length > 0) ||
      (activeProfile.emergency_contact_number && activeProfile.emergency_contact_number.trim().length > 0)
    );

    return (
      hasFacilities ||
      hasEquipments ||
      hasDoctors ||
      hasNurses ||
      hasEmergency
    );
  }, [activeProfile, linkedDoctors.length]);

  const renderMaintenanceSchemes = (fullWidth: boolean) => {
    if (nibhavYojanaList.length === 0) return null;

    const getSchemeTag = (title: string) => {
      const lower = title.toLowerCase();
      if (lower.includes("main pillar") || lower.includes("મુખ્ય સ્તંભ"))
        return lang === "gu" ? "મુખ્ય સ્તંભ" : "Main Pillar";
      if (
        lower.includes("co-pillar") ||
        lower.includes("co-supporting") ||
        lower.includes("સહ સ્તંભ")
      )
        return lang === "gu" ? "સહ સ્તંભ" : "Co-Pillar";
      if (lower.includes("supporting pillar") || lower.includes("આધાર સ્તંભ"))
        return lang === "gu" ? "આધાર સ્તંભ" : "Supporting Pillar";
      if (lower.includes("pillar") || lower.includes("સ્તંભ"))
        return lang === "gu" ? "સ્તંભ સહાય" : "Pillar Support";
      if (
        lower.includes("lifetime") ||
        lower.includes("permanent") ||
        lower.includes("આજીવન") ||
        lower.includes("કાયમી")
      )
        return lang === "gu" ? "આજીવન" : "Lifetime";
      if (
        lower.includes("10 year") ||
        lower.includes("10 years") ||
        lower.includes("૧૦ વર્ષ")
      )
        return lang === "gu" ? "૧૦ વર્ષ" : "10 Years";
      if (
        lower.includes("5 year") ||
        lower.includes("5 years") ||
        lower.includes("૫ વર્ષ")
      )
        return lang === "gu" ? "૫ વર્ષ" : "5 Years";
      if (
        lower.includes("1 year") ||
        lower.includes("1 years") ||
        lower.includes("૧ વર્ષ")
      )
        return lang === "gu" ? "૧ વર્ષ" : "1 Year";
      if (lower.includes("tithi") || lower.includes("તિથિ"))
        return lang === "gu" ? "તિથિ" : "Tithi";
      if (
        lower.includes("camp") ||
        lower.includes("check-up") ||
        lower.includes("કેમ્પ")
      )
        return lang === "gu" ? "કેમ્પ સહાય" : "Camp Support";
      if (
        lower.includes("surgery") ||
        lower.includes("surgeries") ||
        lower.includes("cataract") ||
        lower.includes("ઓપરેશન") ||
        lower.includes("મોતિયા")
      )
        return lang === "gu" ? "ઓપરેશન સહાય" : "Surgery Support";
      if (lower.includes("chief") || lower.includes("મુખ્ય દાતા"))
        return lang === "gu" ? "મુખ્ય દાતા" : "Chief Donor";
      if (lower.includes("supporting") || lower.includes("સહાયક"))
        return lang === "gu" ? "સહાયક દાતા" : "Supporting";
      return lang === "gu" ? "નિભાવ" : "Maintenance";
    };

    return (
      <Reveal className="border-border/60 shadow-xs space-y-6 rounded-3xl border bg-card p-6 md:p-8">
        <div className="border-l-3 border-[#0052b4] py-0.5 pl-3">
          <h3 className="font-serif text-xl font-extrabold text-foreground md:text-2xl">
            {lang === "gu" ? "નિભાવ યોજનાઓ" : "Maintenance Schemes"}
          </h3>
          <p className="mt-1 text-xs text-muted-foreground md:text-sm">
            {lang === "gu"
              ? "સંભાળ અને નિભાવ સહાય યોજનાઓ — આરોગ્ય સેવામાં સીધું યોગદાન આપો"
              : "Care & Maintenance Support Schemes — Contribute directly towards healthcare seva"}
          </p>
        </div>

        {/* Table Container */}
        <div className="overflow-hidden rounded-2xl border border-border/70 bg-card shadow-xs sm:rounded-3xl">
          {/* Table Header (Desktop / Tablet view) */}
          <div className="hidden grid-cols-[40px_2.8fr_2.4fr_1.2fr_115px] items-center gap-4 border-b border-border/70 bg-background px-6 py-3.5 text-[11px] font-extrabold uppercase tracking-wider text-primary lg:grid">
            <div className="text-center">{lang === "gu" ? "ક્રમ" : "#"}</div>
            <div>{lang === "gu" ? "સેવા" : "SERVICE"}</div>
            <div>
              {lang === "gu" ? "વર્ણન / વર્ગ" : "DESCRIPTION / CATEGORY"}
            </div>
            <div>{lang === "gu" ? "રકમ" : "AMOUNT"}</div>
            <div className="text-right pr-1">
              {lang === "gu" ? "ક્રિયા" : "ACTION"}
            </div>
          </div>

          {/* Table Body Rows */}
          <Stagger step={70} className="divide-y divide-border/40">
            {nibhavYojanaList.map((scheme, index) => {
              const tag = getSchemeTag(scheme.title);

              return (
                <StaggerItem
                  key={index}
                  index={index}
                  className="group px-4 py-3 transition-colors duration-150 hover:bg-background sm:px-6 sm:py-3.5"
                >
                  {/* Desktop Row */}
                  <div className="hidden grid-cols-[40px_2.8fr_2.4fr_1.2fr_115px] items-center gap-4 lg:grid">
                    {/* 1. # Index */}
                    <div className="text-center text-xs font-medium text-muted-foreground">
                      {index + 1}
                    </div>

                    {/* 2. Service Name (Icon Removed) */}
                    <div className="pr-2">
                      <span className="text-[13.5px] font-semibold text-foreground leading-snug">
                        {scheme.title}
                      </span>
                    </div>

                    {/* 3. Description / Category */}
                    <div className="text-xs text-muted-foreground pr-2">
                      {tag}
                    </div>

                    {/* 4. Amount */}
                    <div className="font-sans text-sm font-extrabold text-primary">
                      ₹{Number(scheme.price).toLocaleString("en-IN")}
                    </div>

                    {/* 5. Action */}
                    <div className="flex justify-end pr-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full max-w-[105px] rounded-full border-primary/40 bg-background px-3 py-1 text-[11px] font-bold text-primary shadow-xs transition-all hover:bg-primary hover:text-primary-foreground hover:shadow-sm"
                        onClick={() => handleDonateScheme(scheme)}
                      >
                        {lang === "gu" ? "પ્રાયોજન કરો" : "Sponsor Now"}
                      </Button>
                    </div>
                  </div>

                  {/* Mobile / Tablet Card View */}
                  <div className="flex flex-col gap-2.5 lg:hidden">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2">
                        <span className="text-[11px] font-bold text-muted-foreground/80 mt-0.5">
                          #{index + 1}
                        </span>
                        <div>
                          <h4 className="font-sans text-[13px] font-bold text-foreground leading-snug">
                            {scheme.title}
                          </h4>
                          {tag && (
                            <p className="mt-0.5 text-[11px] text-muted-foreground">
                              {tag}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0 text-right font-sans text-sm font-extrabold text-primary">
                        ₹{Number(scheme.price).toLocaleString("en-IN")}
                      </div>
                    </div>

                    <div className="flex justify-end pt-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full border-primary/40 bg-background px-3.5 py-1 text-[11px] font-bold text-primary shadow-xs transition-all hover:bg-primary hover:text-primary-foreground"
                        onClick={() => handleDonateScheme(scheme)}
                      >
                        {lang === "gu" ? "પ્રાયોજન કરો" : "Sponsor Now"}
                      </Button>
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </Reveal>
    );
  };

  if (loading) {
    return (
      <PageShell>
        <div className="flex min-h-[50vh] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </PageShell>
    );
  }

  if (!activeProfile) {
    return (
      <PageShell>
        <div className="mx-auto max-w-site px-4 py-16 text-center">
          <Stethoscope className="mx-auto h-12 w-12 animate-pulse text-muted-foreground" />
          <h2 className="mt-4 font-serif text-2xl font-extrabold text-foreground">
            Healthcare Unit Not Found
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            The requested medical center could not be loaded or is currently
            inactive.
          </p>
          <div className="mt-6">
            <Link
              to="/healthcare"
              className="hover:bg-primary/90 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-xs font-bold text-primary-foreground shadow-sm"
            >
              <ArrowLeft className="h-4 w-4" /> Back to Healthcare Units
            </Link>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="animate-fade-in mx-auto max-w-site space-y-12 px-4 pb-16 pt-8">
        {/* HEADER TITLE SECTION */}
        <div className="border-border/60 shadow-xs relative overflow-hidden rounded-3xl border bg-card p-6 md:p-10">
          {/* Background image slideshow */}
          <div className="anim-bg-settle absolute inset-0 z-0">
            {bgImages.map((src, idx) => (
              <img
                key={idx}
                src={src}
                alt=""
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${idx === bgIndex ? "opacity-50" : "opacity-0"
                  }`}
              />
            ))}
            {/* Elegant dark overlay gradient to blend text and background */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/50 to-black/20" />
          </div>

          {/* Header Content */}
          <div className="relative z-10 flex flex-col items-center gap-6 text-center">
            {/* Back Button and Badge Row */}
            <div
              className="anim-fade-up flex w-full flex-col items-center justify-between gap-4 sm:flex-row sm:items-center"
              style={{ animationDelay: "150ms" }}
            >
              <Link
                to="/healthcare"
                className="shadow-2xs backdrop-blur-xs group inline-flex items-center gap-2 rounded-xl border-white/20 bg-white/10 px-3.5 py-2 text-xs font-bold text-white transition-all duration-300 hover:-translate-x-0.5 hover:border-white/40 hover:bg-white/20"
              >
                <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-0.5" />
                Back to Healthcare Units
              </Link>

              <Badge
                variant="secondary"
                className="border-none bg-[#0052b4] px-3 py-0.5 text-xs font-bold text-white shadow-sm"
              >
                {activeProfile.highlights?.[0]?.title || "Care Services"}
              </Badge>

              {/* Spacer to balance the layout on desktop view */}
              <div className="hidden w-[180px] sm:block" aria-hidden="true" />
            </div>

            <div className="max-w-3xl space-y-3">
              <h1 className="font-serif text-3xl font-extrabold tracking-tight !text-white drop-shadow-md sm:text-4xl">
                <span className="anim-line" style={{ animationDelay: "280ms" }}>
                  <span>{activeProfile.hospital_name}</span>
                </span>
              </h1>
              <p
                className="anim-fade-up text-center text-sm font-medium leading-relaxed text-slate-200 drop-shadow-sm"
                style={{ animationDelay: "450ms" }}
              >
                {activeProfile.subtitle}
              </p>

              {/* Quick Info Row */}
              <div
                className="anim-fade-up flex flex-wrap items-center justify-center gap-x-4 gap-y-2.5 pt-4 text-xs font-semibold"
                style={{ animationDelay: "580ms" }}
              >
                {activeProfile.hospital_timings && (
                  <div className="shadow-2xs backdrop-blur-xs flex items-center gap-2 rounded-xl border-white/20 bg-black/30 px-3 py-1.5 text-white">
                    <Clock className="h-3.5 w-3.5" />
                    <span>{activeProfile.hospital_timings}</span>
                  </div>
                )}
                {activeProfile.closed_on && (
                  <div className="shadow-2xs backdrop-blur-xs flex items-center gap-2 rounded-xl border-amber-500/20 bg-amber-600/50 px-3 py-1.5 text-amber-200">
                    <CalendarX className="h-3.5 w-3.5" />
                    <span>Closed on: {activeProfile.closed_on}</span>
                  </div>
                )}
                {/* {(activeProfile.new_patient_fee ||
                  activeProfile.existing_patient_fee) && (
                    <div className="shadow-2xs flex items-center gap-2 rounded-xl  border-emerald-500/20 bg-emerald-600/50 px-3 py-1.5 text-emerald-200 backdrop-blur-xs">
                      <Coins className="h-3.5 w-3.5" />
                      <span>
                        {activeProfile.new_patient_fee
                          ? `New Patient: ₹${parseInt(String(activeProfile.new_patient_fee))}`
                          : ""}
                        {activeProfile.new_patient_fee &&
                          activeProfile.existing_patient_fee
                          ? " | "
                          : ""}
                        {activeProfile.existing_patient_fee
                          ? `Existing: ₹${parseInt(String(activeProfile.existing_patient_fee))}`
                          : ""}
                      </span>
                    </div>
                  )} */}
              </div>
            </div>
          </div>
        </div>

        {/* 1st in Gujarat Tagline Strip (Highlighted for Physiotherapy Center) */}
        {isPhysioCenter && (
          <Reveal
            variant="zoom"
            className="border-amber-400/50 shadow-xs relative overflow-hidden rounded-2xl border bg-gradient-to-r from-amber-500/10 via-amber-500/20 to-orange-500/10 p-3.5 text-center transition-all duration-300 hover:border-amber-500/70 sm:p-4"
          >
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#ffbd3d] text-gray-900 shadow-xs">
                <Award className="h-4 w-4" />
              </div>
              <span
                className={`!font-bold !font-sans !text-base tracking-wide text-amber-900 dark:text-amber-200 ${lang === "gu"
                  ? "!font-sans text-base sm:text-lg"
                  : "font-serif text-base sm:text-lg md:text-xl"
                  }`}
              >
                {lang === "gu"
                  ? "ગુજરાતમાં પ્રથમ - પક્ષઘાત પુનર્વસન ફિઝિયોથેરાપી સેન્ટર"
                  : "1st in Gujarat - Paralysis Rehabilitation Physiotherapy Center"}
              </span>
            </div>
          </Reveal>
        )}

        {/* Main Content Layout Block: Two Columns Grid if right side has content, otherwise full-width */}
        <div className={hasRightSideContent ? "grid gap-8 lg:grid-cols-3" : "space-y-8 w-full"}>
          {/* Main Panel Column (Left 2/3 width or 100% full-width if no right content) */}
          <div className={hasRightSideContent ? "space-y-8 lg:col-span-2" : "space-y-8 w-full"}>
            <div className="space-y-8">
              {/* About Section */}
              {(activeProfile.image ||
                activeProfile.description ||
                activeProfile.poem) && (
                  <Reveal className="border-border/60 shadow-xs space-y-4 rounded-3xl border bg-card p-6">
                    <div className="border-l-3 border-[#0052b4] py-0.5 pl-3">
                      <h3 className="font-serif text-lg font-extrabold text-foreground">
                        About This Facility
                      </h3>
                    </div>
                    <div className="flex flex-col gap-6 md:flex-row">
                      {activeProfile.image &&
                        typeof activeProfile.image === "string" && (
                          <ImageReveal
                            direction="left"
                            delay={150}
                            className="w-full rounded-2xl md:w-64"
                          >
                            <img
                              src={activeProfile.image}
                              alt={activeProfile.hospital_name}
                              className="h-48 w-full rounded-2xl border border-border object-cover md:w-64"
                            />
                          </ImageReveal>
                        )}
                      <div className="flex-1 space-y-3">
                        {activeProfile.description && (
                          <p className="whitespace-pre-wrap text-xs font-semibold leading-relaxed text-foreground">
                            {activeProfile.description}
                          </p>
                        )}
                        {activeProfile.poem && (
                          <blockquote className="my-2 rounded-r-2xl border-l-4 border-sky-400 bg-amber-500/10 p-3.5 dark:bg-amber-500/15 sm:p-4">
                            <p className="whitespace-pre-wrap text-sm font-semibold italic leading-relaxed text-[#0a2540] dark:text-amber-100 sm:text-base">
                              {`“${activeProfile.poem.replace(/^[“"]|[”"]$/g, "").trim()}”`}
                            </p>
                          </blockquote>
                        )}
                      </div>
                    </div>
                  </Reveal>
                )}

              {/* Section 2: Treatments & Medical Services */}
              <Reveal
                variant="right"
                className="border-border/60 shadow-xs space-y-4 rounded-3xl border bg-card p-6"
              >
                <div className="border-l-3 border-[#0052b4] py-0.5 pl-3">
                  <h3 className="font-serif text-lg font-extrabold text-foreground">
                    Treatments &amp; Medical Services
                  </h3>
                </div>
                {activeProfile.treatments &&
                  activeProfile.treatments.length > 0 ? (
                  <Stagger
                    step={60}
                    className={`grid gap-x-6 gap-y-2.5 sm:grid-cols-2 ${
                      hasRightSideContent ? "" : "md:grid-cols-3 lg:grid-cols-4"
                    }`}
                  >
                    {activeProfile.treatments.map((item, idx) => (
                      <StaggerItem
                        key={idx}
                        index={idx}
                        from="left"
                        className="flex items-start gap-2 text-xs font-semibold text-foreground"
                      >
                        <span className="font-bold text-[#0052b4]">•</span>
                        <span className="leading-snug">{item}</span>
                      </StaggerItem>
                    ))}
                  </Stagger>
                ) : (
                  <p className="text-xs italic text-muted-foreground">
                    No specific treatments listed.
                  </p>
                )}
              </Reveal>

              {/* Section 3: A Sacred Seva Yagna & Humanitarian Story (Only for Medical OPD Centre) */}
              {isMedicalOpdCenter && (
                <Reveal
                  variant="morph"
                  className="border-border/60 shadow-xs space-y-6 rounded-3xl border bg-card p-6 md:p-8"
                >
                  {/* Header Title */}
                  <div className="space-y-3">
                    <div className="border-l-3 border-[#0052b4] py-0.5 pl-3">
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
                        {t("care.healthcare.sevaYajna.badge")}
                      </p>
                      <h3
                        className={`mt-1 font-bold text-foreground ${
                          lang === "gu"
                            ? "!font-sans text-xl sm:text-2xl"
                            : "font-serif text-xl sm:text-2xl"
                        }`}
                      >
                        {t("care.healthcare.sevaYajna.title")}
                      </h3>
                    </div>
                  </div>

                  {/* Story Narrative Paragraphs */}
                  <Stagger
                    step={140}
                    className="grid gap-4 text-xs leading-relaxed text-muted-foreground sm:text-sm md:grid-cols-3"
                  >
                    <StaggerItem
                      index={0}
                      from="left"
                      className="border-border/70 shadow-2xs space-y-2 rounded-2xl border bg-background p-4 sm:p-5"
                    >
                      <div className="flex items-center gap-2 font-bold text-foreground">
                        <HeartPulse className="h-4 w-4 text-rose-500" />
                        <span>
                          {lang === "gu"
                            ? "મોંઘી તબીબી સારવારની વાસ્તવિકતા"
                            : "Rising Healthcare Costs"}
                        </span>
                      </div>
                      <p>{t("care.healthcare.sevaYajna.p1")}</p>
                    </StaggerItem>

                    <StaggerItem
                      index={1}
                      className="border-border/70 shadow-2xs space-y-2 rounded-2xl border bg-background p-4 sm:p-5"
                    >
                      <div className="flex items-center gap-2 font-bold text-foreground">
                        <Building2 className="h-4 w-4 text-amber-600" />
                        <span>
                          {lang === "gu"
                            ? "વર્ષ ૨૦૦૨ થી ૨૯૮+ વડીલોનો આશ્રય"
                            : "Serving Since 2002"}
                        </span>
                      </div>
                      <p>{t("care.healthcare.sevaYajna.p2")}</p>
                    </StaggerItem>

                    <StaggerItem
                      index={2}
                      from="right"
                      className="border-border/70 shadow-2xs space-y-2 rounded-2xl border bg-background p-4 sm:p-5"
                    >
                      <div className="flex items-center gap-2 font-bold text-foreground">
                        <Award className="h-4 w-4 text-primary" />
                        <span>
                          {lang === "gu"
                            ? "૨૦૧૧ માં મેડિકલ સેન્ટરની સ્થાપના"
                            : "Founded in 2011"}
                        </span>
                      </div>
                      <p>{t("care.healthcare.sevaYajna.p3")}</p>
                    </StaggerItem>
                  </Stagger>

                  {/* Free Care & Monthly Expenditure Callouts */}
                  <Reveal variant="fade" className="grid gap-4 sm:grid-cols-2">
                    <div className="flex items-start gap-3.5 rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.04] p-4 text-xs sm:p-5 sm:text-sm">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                      <div>
                        <span className="mb-1 block font-bold text-emerald-900 dark:text-emerald-300">
                          {lang === "gu"
                            ? "સંપૂર્ણપણે નિ:શુલ્ક અને ટોકન દરે સેવા"
                            : "100% Free & Token Rate Service"}
                        </span>
                        <p className="leading-relaxed text-muted-foreground">
                          {t("care.healthcare.sevaYajna.freeCareNote")}
                        </p>
                      </div>
                    </div>

                    <div className="border-primary/20 bg-primary/[0.04] flex items-start gap-3.5 rounded-2xl border p-4 text-xs sm:p-5 sm:text-sm">
                      <Heart className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                      <div>
                        <span className="mb-1 block font-bold text-foreground">
                          {lang === "gu"
                            ? "સેવા યજ્ઞમાં સહભાગી બનો"
                            : "Partner in Sacred Seva"}
                        </span>
                        <p className="leading-relaxed text-muted-foreground">
                          {t("care.healthcare.sevaYajna.expenseNote")}
                        </p>
                      </div>
                    </div>
                  </Reveal>
                </Reveal>
              )}
            </div>

            {/* When right side HAS content sections, Maintenance Schemes sits on the left side only to balance */}
            {hasRightSideContent && renderMaintenanceSchemes(false)}
          </div>

          {/* Sidebar Column (Right 1/3 width) - Only rendered when right side has content sections */}
          {hasRightSideContent && (
            <Reveal variant="right" delay={150} className="space-y-6">
              {/* Sidebar Donation Card */}
              <div className="border-border/60 shadow-xs space-y-4 rounded-3xl border bg-card p-6">
                <div className="border-l-3 border-rose-600 py-0.5 pl-3">
                  <h3 className="font-serif text-base font-extrabold text-foreground">
                    Support &amp; Donate
                  </h3>
                </div>
                <p className="text-xs text-muted-foreground">
                  Support this healthcare unit with a donation to help maintain
                  services and equipment.
                </p>
                <div className="pt-2">
                  <Button
                    onClick={() => setDonationModalOpen(true)}
                    className="shadow-xs btn-lift flex w-full items-center justify-center gap-2 rounded-xl bg-[#0052b4] px-4 py-3 text-sm font-bold text-white transition-all duration-300 hover:bg-[#0052b4]/90"
                  >
                    <HeartPulse className="h-4 w-4" /> Donate
                  </Button>
                </div>
              </div>

              {/* Facilities Provided */}
              {activeProfile.facilities &&
                activeProfile.facilities.length > 0 && (
                  <div className="border-border/60 shadow-xs space-y-4 rounded-3xl border bg-card p-6">
                    <div className="border-l-3 border-[#0052b4] py-0.5 pl-3">
                      <h3 className="font-serif text-base font-extrabold text-foreground">
                        Facilities Provided
                      </h3>
                    </div>
                    <div className="space-y-3">
                      {activeProfile.facilities.map((fac, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2.5 text-xs font-semibold text-foreground"
                        >
                          <span className="font-bold text-[#0052b4]">•</span>
                          <span>{fac}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Advanced Equipment & Infrastructure */}
              {activeProfile.equipments &&
                activeProfile.equipments.length > 0 && (
                  <div className="border-border/60 shadow-xs space-y-4 rounded-3xl border bg-card p-6">
                    <div className="border-l-3 border-[#0052b4] py-0.5 pl-3">
                      <h3 className="font-serif text-base font-extrabold text-foreground">
                        Advanced Equipment &amp; Infrastructure
                      </h3>
                    </div>
                    <div className="space-y-2.5">
                      {activeProfile.equipments.map((eq, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-2 text-xs font-semibold leading-snug text-foreground"
                        >
                          <span className="shrink-0 font-bold text-[#0052b4]">
                            •
                          </span>
                          <span>{eq}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Attending Doctors & Specialists */}
              {linkedDoctors.length > 0 && (
                <div className="border-border/60 shadow-xs space-y-4 rounded-3xl border bg-card p-6">
                  <div className="border-l-3 border-[#0052b4] py-0.5 pl-3">
                    <h3 className="font-serif text-base font-extrabold text-foreground">
                      Attending Doctors &amp; Specialists
                    </h3>
                  </div>
                  <div className="space-y-3.5">
                    {linkedDoctors.map(doc => (
                      <div
                        key={doc.id}
                        className="border-border/80 shadow-2xs flex flex-col justify-between space-y-3 rounded-2xl border bg-background p-4 transition-colors hover:border-[#0052b4]/40"
                      >
                        <div className="space-y-1.5">
                          <div>
                            <h4 className="font-serif text-sm font-bold text-foreground">
                              {doc.name}
                            </h4>
                            {doc.qualification && (
                              <Badge
                                variant="secondary"
                                className="mt-1 bg-[#0052b4]/10 text-[10px] font-semibold text-[#0052b4]"
                              >
                                {doc.qualification}
                              </Badge>
                            )}
                          </div>
                          {doc.designation && (
                            <p className="text-xs font-medium text-muted-foreground">
                              {doc.designation}
                            </p>
                          )}
                        </div>
                        <div className="border-border/50 rounded-xl border-t bg-amber-500/10 p-2.5 pt-2 text-[11px] font-semibold text-amber-800">
                          <span>
                            Timings:{" "}
                            {doc.timings || activeProfile.hospital_timings}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Nursing & Care Staff */}
              {activeProfile.nurses && activeProfile.nurses.length > 0 && (
                <div className="border-border/60 shadow-xs space-y-4 rounded-3xl border bg-card p-6">
                  <div className="border-l-3 border-emerald-600 py-0.5 pl-3">
                    <h3 className="font-serif text-base font-extrabold text-foreground">
                      Nursing &amp; Care Staff
                    </h3>
                  </div>
                  <div className="space-y-3.5">
                    {activeProfile.nurses.map((staff, idx) => (
                      <div
                        key={idx}
                        className="border-border/40 flex flex-col border-b pb-2 last:border-0 last:pb-0"
                      >
                        <span className="text-xs font-bold text-foreground">
                          {staff.name}
                        </span>
                        <span className="mt-0.5 text-[10px] font-medium text-muted-foreground">
                          {staff.role}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Emergency Call Box */}
              {(activeProfile.emergency_contact_name ||
                activeProfile.emergency_contact_number) && (
                <div className="shadow-xs space-y-4 rounded-3xl border border-amber-500/30 p-6">
                  <div>
                    <p className="text-[10px] font-extrabold uppercase tracking-wider text-amber-900">
                      Emergency Medical Contact
                    </p>
                    {activeProfile.emergency_contact_name && (
                      <p className="mt-1.5 text-sm font-extrabold leading-snug text-foreground">
                        {activeProfile.emergency_contact_name}
                      </p>
                    )}
                  </div>
                  {activeProfile.emergency_contact_number && (
                    <a
                      href={`tel:${activeProfile.emergency_contact_number}`}
                      className="shadow-2xs block rounded-xl bg-amber-500/20 py-3 text-center text-xs font-black text-amber-800 transition-colors hover:bg-amber-500/25 hover:underline"
                    >
                      📞 Call: {activeProfile.emergency_contact_number}
                    </a>
                  )}
                </div>
              )}
            </Reveal>
          )}
        </div>

        {/* When right side has NO content, Maintenance Schemes and Support & Donate are full-width */}
        {!hasRightSideContent && (
          <div className="space-y-8">
            {/* Full-width Support & Donate Banner */}
            <Reveal
              variant="morph"
              className="border-border/60 shadow-xs flex flex-col md:flex-row items-center justify-between gap-6 rounded-3xl border bg-card p-6 md:p-8"
            >
              <div className="space-y-1.5">
                <div className="border-l-3 border-rose-600 py-0.5 pl-3">
                  <h3 className="font-serif text-lg font-extrabold text-foreground md:text-xl">
                    Support &amp; Donate
                  </h3>
                </div>
                <p className="text-xs text-muted-foreground md:text-sm pl-4">
                  Support this healthcare unit with a donation to help maintain services and equipment.
                </p>
              </div>
              <Button
                onClick={() => setDonationModalOpen(true)}
                className="shadow-xs btn-lift shrink-0 flex items-center justify-center gap-2 rounded-xl bg-[#0052b4] px-6 py-3 text-sm font-bold text-white transition-all duration-300 hover:bg-[#0052b4]/90"
              >
                <HeartPulse className="h-4 w-4" /> Donate Now
              </Button>
            </Reveal>

            {/* Full-width Maintenance Schemes */}
            {renderMaintenanceSchemes(true)}
          </div>
        )}

        {/* HIGHLIGHT CARDS STRIP */}
        {activeProfile.highlights && activeProfile.highlights.length > 0 && (
          <Reveal variant="fade" className="space-y-4">
            <div className="text-center text-base font-extrabold uppercase tracking-wider text-amber-700">
              Key Highlights &amp; Accomplishments
            </div>
            <Marquee pauseOnHover speed={30} gradient={false} className="py-2">
              {activeProfile.highlights.map((hl, idx) => (
                <div
                  key={idx}
                  className="shadow-xs mx-3 w-[290px] flex-shrink-0 space-y-1.5 rounded-2xl border border-border bg-card p-5 transition-all duration-300 hover:-translate-y-1 hover:border-[#0052b4]/40 hover:shadow-md"
                >
                  <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#0052b4]">
                    {hl.title || "KEY HIGHLIGHT"}
                  </p>
                  <p className="text-xs font-bold leading-snug text-foreground">
                    {hl.description}
                  </p>
                </div>
              ))}
            </Marquee>
          </Reveal>
        )}
      </div>

      <HealthcareDonationModal
        open={donationModalOpen}
        onOpenChange={open => {
          setDonationModalOpen(open);
          if (!open) {
            setSelectedScheme(null);
          }
        }}
        hospitalName={activeProfile.hospital_name}
        hospitalId={activeProfile.id}
        initialAmount={selectedScheme?.price}
        maintenance_schemes={selectedScheme?.title}
      />
    </PageShell>
  );
}
